import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { getSEOData } from '../config/seo';
import { trackEvent, trackButtonClick } from '../components/GoogleAnalytics';
import { 
  ArrowRight, 
  Play, 
  Star, 
  Shield, 
  Globe,
  Users,
  CheckCircle,
  BarChart3,
  Award,
  Workflow,
  Database,
  Cloud,
  TrendingUp,
  Lock,
  Headphones
} from 'lucide-react';
import { YOUTUBE_CHANNEL, PLATFORM_VIDEOS, createYouTubeEmbedUrl, handleMissingVideo } from '../config/videos';

const Home = () => {
  const seoData = getSEOData('/');

  // Track page view
  useEffect(() => {
    trackEvent('page_view', 'engagement', 'home_page');
  }, []);

  return (
    <div className="min-h-screen">
      <SEO {...seoData} />
      {/* Hero Section - Enterprise Grade */}
      <section className="relative bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              {/* Enterprise Badge */}
              <div className="inline-flex items-center bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full px-6 py-2 mb-6">
                <Award className="h-4 w-4 text-primary mr-2" />
                <span className="text-sm font-semibold text-gray-700">Trusted by Enterprise Clients Worldwide</span>
              </div>

              <h1 className="text-4xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight mb-6">
                Composable Enterprise
                <span className="block bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">
                  Automation Platform
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-600 mb-8 leading-relaxed">
                Build modular, API-first business capabilities that adapt to change. Create composable applications 
                with reusable components, automate workflows with packaged business capabilities.
              </p>

              {/* Key Value Props */}
              <div className="flex flex-wrap gap-6 mb-10">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700 font-medium">Packaged Business Capabilities</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700 font-medium">API-First Architecture</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700 font-medium">Modular Component Library</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a
                  href="https://app.aquargin.com/register"
                  onClick={() => trackButtonClick('start_free_trial', 'hero_section')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </motion.a>
                
                <motion.a
                  href={YOUTUBE_CHANNEL.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick('watch_demo', 'hero_section')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-primary hover:text-primary transition-colors duration-300 flex items-center justify-center group"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Platform Demo
                </motion.a>
              </div>

              {/* Trust Indicators */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-8 opacity-70">
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-yellow-500 mr-1" />
                    <span className="text-sm font-medium">4.9/5 Rating</span>
                  </div>
                  <div className="flex items-center">
                    <Shield className="h-5 w-5 text-green-500 mr-1" />
                    <span className="text-sm font-medium">Enterprise Security</span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="h-5 w-5 text-blue-500 mr-1" />
                    <span className="text-sm font-medium">Global Presence</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Hero Platform Image */}
              <div className="relative">
                <img
                  src="/images/A_modern_corporate_office_environment_with_a_confi_1750595349.png"
                  alt="Modern Corporate Office with Holographic Displays"
                  className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100"
                />
                <div className="absolute top-4 right-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                  Live Platform
                </div>
              </div>
              
              {/* Dashboard Preview */}
              <div className="mt-8 bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <img
                  src="/images/Close-up_view_of_a_sleek_modern_laptop_screen_disp_1750595380.png"
                  alt="Enterprise Dashboard Interface"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enterprise Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
                              <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">Global</div>
              <div className="text-gray-600 font-medium">Enterprise Clients</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="text-4xl lg:text-5xl font-bold text-secondary mb-2">1000+</div>
              <div className="text-gray-600 font-medium">Automation Projects</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-4xl lg:text-5xl font-bold text-accent mb-2">99.9%</div>
              <div className="text-gray-600 font-medium">Platform Uptime</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="text-4xl lg:text-5xl font-bold text-green-500 mb-2">24/7</div>
              <div className="text-gray-600 font-medium">Expert Support</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities - Enterprise Focus */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Composable Business Architecture
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Build with modular, reusable components that can be assembled and reassembled to meet changing business needs. 
              Our composable platform enables 30% faster innovation through packaged business capabilities and API-first design.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                icon: Workflow,
                title: "Intelligent Automation",
                description: "AI-powered workflows that adapt and optimize automatically, reducing manual work by up to 80%.",
                features: ["Smart Process Mining", "Predictive Analytics", "Auto-Optimization"],
                image: "/images/Visual_workflow_builder_with_drag-and-drop_interfa_1750595634.png"
              },
              {
                icon: Database,
                title: "Unified Data Platform",
                description: "Connect all your data sources in real-time with enterprise-grade security and compliance.",
                features: ["Real-time Integration", "Data Governance", "Advanced Analytics"],
                image: "/images/3D_isometric_view_of_structured_data_cubes_floatin_1750595537.png"
              },
              {
                icon: Cloud,
                title: "Scalable Infrastructure",
                description: "Built on enterprise-grade cloud infrastructure that scales with your business growth.",
                features: ["Auto-scaling", "Global CDN", "99.9% SLA"],
                image: "/images/Modern_technology_stack_representation_with_layers_1750596488.png"
              }
            ].map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={capability.image} 
                    alt={capability.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-8">
                  <div className="bg-gradient-to-r from-primary/10 to-secondary/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <capability.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{capability.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{capability.description}</p>
                  
                  <div className="mb-6">
                    <a 
                      href={index === 0 ? '/features/workflow-automation' : index === 1 ? '/features/data-management' : '/technical-architecture'}
                      className="inline-flex items-center text-primary hover:text-primary-600 font-medium transition-colors"
                    >
                      Learn More
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </div>
                </div>
                <ul className="space-y-2">
                  {capability.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Solutions Showcase */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Enterprise-Grade Solutions
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Purpose-built applications for every business function, all integrated on one platform.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {[
                {
                  icon: Users,
                  title: "AquaCRM",
                  description: "Complete customer relationship management with AI-powered insights and automation."
                },
                {
                  icon: BarChart3,
                  title: "AquaERP",
                  description: "Comprehensive enterprise resource planning for finance, operations, and supply chain."
                },
                {
                  icon: TrendingUp,
                  title: "AquaAnalytics",
                  description: "Advanced business intelligence with real-time dashboards and predictive analytics."
                },
                {
                  icon: Lock,
                  title: "AquaSecurity",
                  description: "Enterprise-grade security and compliance management across all business processes."
                }
              ].map((solution, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-r from-primary/10 to-secondary/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <solution.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{solution.title}</h3>
                    <p className="text-gray-600">{solution.description}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Platform Integration</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm text-green-600 font-medium">Live</span>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Data Synchronization</span>
                      <span className="text-sm text-green-600">Active</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Workflow Automation</span>
                      <span className="text-sm text-green-600">Running</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">API Connections</span>
                      <span className="text-sm text-green-600">Healthy</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video Showcase - Enterprise Focus */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              See Aquargin in Action
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how leading enterprises are transforming their operations with Aquargin's automation platform.
            </p>
          </div>

          <div className="grid lg:grid-cols-1 gap-8">
            {/* Platform Demo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="relative bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={createYouTubeEmbedUrl(PLATFORM_VIDEOS.overview.id)}
                  title={PLATFORM_VIDEOS.overview.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                  onClick={() => window.open(YOUTUBE_CHANNEL.url, '_blank')}
                />
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{PLATFORM_VIDEOS.overview.title}</h3>
                <p className="text-gray-600">{PLATFORM_VIDEOS.overview.description}</p>
              </div>
            </motion.div>

            {/* Customer Success Story */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative group"
            >
              <div className="relative bg-gradient-to-br from-secondary/10 to-accent/10 rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={createYouTubeEmbedUrl(PLATFORM_VIDEOS.overview.id)}
                  title="Customer Success Story - Enterprise Transformation"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                  onClick={() => window.open(YOUTUBE_CHANNEL.url, '_blank')}
                />
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Enterprise Transformation Story</h3>
                <p className="text-gray-600">See how leading enterprises transform their operations with Aquargin's automation platform.</p>
              </div>
            </motion.div> */}
          </div>

          {/* YouTube Channel CTA */}
          <div className="text-center mt-12">
            <motion.a
              href={YOUTUBE_CHANNEL.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center bg-red-50 text-red-700 px-6 py-3 rounded-full hover:bg-red-100 transition-colors cursor-pointer"
            >
              <Play className="h-5 w-5 mr-2" />
              <span className="font-semibold">Subscribe to {YOUTUBE_CHANNEL.handle} for more insights</span>
              <ArrowRight className="h-4 w-4 ml-2" />
            </motion.a>
          </div>
        </div>
      </section>

      {/* Enterprise Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Trusted by Industry Leaders
            </motion.h2>
            <p className="text-xl text-gray-600">
              See why leading enterprises choose Aquargin for their digital transformation journey.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                quote: "Aquargin transformed our healthcare operations completely. The automation capabilities reduced our administrative overhead by 60% while improving patient care quality.",
                author: "",
                title: "CTO, Delhi Health Centre",
                company: "Healthcare Enterprise",
                metric: "60% cost reduction"
              },
              {
                quote: "The platform's flexibility and scalability allowed us to automate our entire educational operations. We've seen 40% improvement in student management efficiency.",
                author: "",
                title: "Operations Director, WarpEduTech",
                company: "Educational Technology",
                metric: "40% efficiency gain"
              },
              {
                quote: "Sher-e-bengal achieved 300% ROI within the first year of implementing Aquargin. The business intelligence features are game-changing.",
                author: "",
                title: "CEO, Sher-e-bengal",
                company: "Financial Services",
                metric: "300% ROI achieved"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-gray-900">{testimonial.author}</p>
                      <p className="text-sm text-gray-600">{testimonial.title}</p>
                      <p className="text-sm text-gray-500">{testimonial.company}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">{testimonial.metric}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Access Links */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"
            >
              Explore Our Platform
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover all aspects of the Aquargin platform - from core features to technical architecture
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Platform Features', desc: 'Core capabilities & tools', href: '/features', icon: '⚡' },
              { title: 'Products & Solutions', desc: 'Industry-specific apps', href: '/products', icon: '📦' },
              { title: 'Technical Architecture', desc: 'System design & infrastructure', href: '/technical-architecture', icon: '🏗️' },
              { title: 'Pricing & Plans', desc: 'Enterprise pricing options', href: '/pricing', icon: '💰' },
              { title: 'AI Intelligence', desc: 'Conversational AI & insights', href: '/features/ai-intelligence', icon: '🤖' },
              { title: 'Data Management', desc: 'CRUD operations & relationships', href: '/features/data-management', icon: '📊' },
              { title: 'Workflow Automation', desc: 'Process automation & triggers', href: '/features/workflow-automation', icon: '⚙️' },
              { title: 'Security & Collaboration', desc: 'Access control & team features', href: '/features/security-collaboration', icon: '🔐' }
            ].map((link, index) => (
              <motion.a
                key={index}
                href={link.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-50 hover:bg-primary-50 border border-gray-200 hover:border-primary-200 rounded-lg p-6 transition-all duration-300 group text-center"
              >
                <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">
                  {link.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 mb-2">
                  {link.title}
                </h3>
                <p className="text-sm text-gray-600 mb-3">{link.desc}</p>
                <div className="inline-flex items-center text-primary font-medium text-sm group-hover:translate-x-1 transition-transform">
                  Explore <ArrowRight className="ml-1 h-3 w-3" />
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Enterprise?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Join leading companies who trust Aquargin to power their digital transformation. 
              Start your journey to automated excellence today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/pricing"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-primary px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start Free Enterprise Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary transition-colors duration-300 flex items-center justify-center group"
              >
                <Headphones className="mr-2 h-5 w-5" />
                Talk to Enterprise Expert
              </motion.a>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ No setup fees  ✓ 30-day free trial  ✓ Enterprise support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 